import * as Sentry from "@sentry/nextjs";
import { format } from "date-fns";
import { IntegrationType } from "shared/integrations/integration";
import style from "./monday-list-item-v2.module.css";
import classNames from "classnames";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";
import { useDrag } from "react-dnd";
import { convertToLocalTime } from "shared/util/date-utils";
import { getPathPrefix } from "../../../utils/getPathPrefix";
import { getSelectedStatus } from "utils/monday-utils";
import { MondayLogo } from "frontend/icons/monday-logo";

export function MondayListItemV2({
  item,
  integrationId,
  integrationType,
  configuration,
  onClick,
  isChecked,
  showCheckbox,
  onClickSubitems,
  onDragStart,
}: {
  item: any;
  integrationId: string;
  integrationType: IntegrationType;
  configuration: unknown;
  isChecked: boolean;
  onClick: () => void;
  showCheckbox: boolean;
  onClickSubitems?: () => void;
  onDragStart?: () => void;
}) {
  const [, drag] = useDrag(() => ({
    type: "monday-item",
    item: () => {
      onDragStart?.();
      return { item, integrationType, configuration, integrationId };
    },
    previewOptions: {
      captureDraggingState: true,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  function renderColumnValues() {
    if (!configuration) {
      return null;
    }
    const conf = configuration as any;

    switch (integrationType) {
      case IntegrationType.monday: {
        const dateColumn = conf.config.columns.find((column: any) => column.type === "date");
        const statusColumn = conf.columns.find((col: any) => col.type === "status");
        const dateColumnValue = dateColumn && item.columnValues.find((column: any) => column.id === dateColumn.id);
        const statusColumnValue = getSelectedStatus(item.columnValues, statusColumn);
        const dateRenderValue = dateColumnValue?.value?.date ?? dateColumnValue?.value;
        let formattedDate = null;
        try {
          if (dateColumnValue) {
            formattedDate = format(convertToLocalTime(new Date(dateRenderValue)), "MMM dd");
          }
        } catch (error) {
          Sentry.captureException({ error, dateRenderValue }, { tags: { type: "format-date" } });
        }
        return (
          <>
            {statusColumn && (
              <div className={style.statusValue} style={{ backgroundColor: statusColumnValue.color }}>
                <span>{statusColumnValue.text}</span>
              </div>
            )}
            {formattedDate && (
              <div className={style.dateValue}>
                <img src={getPathPrefix("/images/task-due-date-icon.svg")} />
                <span>{formattedDate}</span>
              </div>
            )}
          </>
        );
      }
    }
  }

  function subitemsClicked(e: any) {
    e.stopPropagation();
    onClickSubitems && onClickSubitems();
  }

  return (
    <div
      data-testid={item.name}
      onClick={onClick}
      className={classNames(style.taskContainer, { [style.checked]: isChecked })}
      ref={drag}
    >
      <div className={style.titleContainer}>
        <div className={style.mondayLogoContainer}>
          <MondayLogo size={20} />
        </div>
        <span className={style.title}>{item.name}</span>
      </div>
      {showCheckbox && (
        <div className={style.checkboxContainer}>
          <Checkbox checked={isChecked} />
        </div>
      )}
      <div className={style.details}>{renderColumnValues()}</div>
      {item.subitems_ids.length > 0 && (
        <div className={style.subitemsCountContainer} onClick={subitemsClicked}>
          <div className={style.subitemsCount}>
            <svg width="12" height="12" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.28711 1V14.7138H9.00119" stroke="currentColor" strokeWidth="1.2" />
              <path
                d="M7.85742 6.71387H3.28616"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
              <path
                d="M5.57129 1L1.00002 1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
              <path d="M9 5H16.9997V8.42845H9V5Z" stroke="currentColor" strokeWidth="1.2" />
              <path d="M9 13H16.9997V16.4284H9V13Z" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            <span>
              Show {item.subitems_ids.length} {item.subitems_ids.length > 1 ? "subitems" : "subitem"}
            </span>
            <span>→</span>
          </div>
        </div>
      )}
    </div>
  );
}
